import { ApiUser, ChatMessage, ChatRequest, HealthResponse, StreamMessage, TodoItem, DocumentListRequest, DocumentListResponse, DocumentDownloadRequest, ThreadListResponse, ThreadMessagesResponse, UserThread, GradingQueryRequest, GradingQueryResponse, AdhocProcessRequest, AdhocProcessResponse, ProcessingStatusResponse } from '../models/types';
import { trackException } from './appInsights';

// API routes are mounted under /api in both local and deployed environments.
const configuredApiBaseUrl = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');
const API_BASE_URL = configuredApiBaseUrl.endsWith('/api')
  ? configuredApiBaseUrl
  : `${configuredApiBaseUrl}/api`;

function normalizeUserThread(record: Record<string, unknown>): UserThread {
  let metadata: Record<string, unknown> = {};
  const rawMetadata = record.Metadata;

  if (typeof rawMetadata === 'string') {
    try {
      metadata = JSON.parse(rawMetadata) as Record<string, unknown>;
    } catch {
      metadata = {};
    }
  } else if (rawMetadata && typeof rawMetadata === 'object') {
    metadata = rawMetadata as Record<string, unknown>;
  }

  const readString = (value: unknown): string | undefined =>
    typeof value === 'string' ? value : undefined;
  const customContext = metadata.custom_context as Record<string, unknown> | undefined;
  const threadId = readString(record.ThreadId) || readString(record.thread_id) || '';
  const createdAt = readString(record.CreatedAt) || readString(record.created_at) || '';

  return {
    Id: typeof record.Id === 'number' ? record.Id : 0,
    UserId: readString(record.UserId) || readString(record.user_id) || '',
    ThreadId: threadId,
    AuthorizationId:
      readString(metadata.authorization_id) || readString(customContext?.authorization_id),
    InitialQuestion: readString(metadata.initial_question),
    Domain: readString(metadata.domain) || readString(customContext?.domain),
    UserEmail: readString(record.UserEmail) || readString(record.user_email),
    CreatedAt: createdAt,
    IsActive: record.IsActive !== false && record.is_active !== false,
    LastUsedAt: readString(record.LastUsedAt) || readString(record.last_used_at),
    RowCreatedTs: readString(record.RowCreatedTs) || createdAt,
    RowModifiedTs: readString(record.RowModifiedTs) || createdAt,
  };
}

async function getApiErrorDetail(response: Response): Promise<string> {
  const responseBody = await response.text();
  if (!responseBody) {
    return response.statusText;
  }

  try {
    const errorResponse = JSON.parse(responseBody) as { detail?: unknown };
    if (typeof errorResponse.detail === 'string') {
      return errorResponse.detail;
    }
    if (errorResponse.detail !== undefined) {
      return JSON.stringify(errorResponse.detail);
    }
  } catch {
    return responseBody;
  }

  return responseBody;
}

// Interface for optional authentication token provider
interface AuthTokenProvider {
  getToken: () => Promise<string | null>;
}

class ChatAPIClass {
  private static authProvider: AuthTokenProvider | null = null;
  private baseURL = API_BASE_URL;

  /**
   * Set authentication token provider for API calls
   */
  setAuthProvider(provider: AuthTokenProvider): void {
    ChatAPIClass.authProvider = provider;
  }

  private static async getAuthHeaders(accessToken?: string): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      return headers;
    }

    if (this.authProvider) {
      try {
        const token = await this.authProvider.getToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('Failed to get auth token:', error);
        // Continue without token rather than failing the request
      }
    }

    return headers;
  }

  private static async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const authHeaders = await this.getAuthHeaders();
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        ...authHeaders,
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorDetail = await getApiErrorDetail(response);
      const errorMessage = `API Error: ${response.status} - ${errorDetail}`;
      
      // Track API errors to Application Insights
      trackException(new Error(errorMessage), {
        endpoint,
        statusCode: response.status,
        userAuthenticated: !!authHeaders['Authorization'],
        errorType: 'api_error'
      });

      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Send a chat message to the backend and get a response
   * Uses the streaming endpoint internally but returns the complete response
   * Requires authentication - all chat endpoints are now secure
   */
  static async sendMessage(
    message: string,
    authNo?: string,
    threadId?: string
  ): Promise<ChatMessage & { thread_id: string }> {
    try {
      // Use the streaming agent endpoint but aggregate the stream into a single result
      const { response: fullResponse, thread_id } = await ChatAPIClass.sendMessageStream(
        message,
        authNo,
        threadId,
        // onChunk: we don't need incremental UI updates here, so ignore chunks
        undefined,
        // onStatus: ignore status updates for this synchronous wrapper
        undefined
      );

      return {
        role: 'assistant',
        content: fullResponse,
        timestamp: new Date().toISOString(),
        thread_id: thread_id || (threadId ?? '')
      };
    } catch (error) {
      console.error('Chat API Error:', error);
      if (error instanceof Error && error.message.includes('401')) {
        throw new Error('Authentication required. Please sign in to use this feature.');
      }
      throw new Error('Failed to send message. Please try again.');
    }
  }

  /**
   * Get current user information (requires authentication)
   */
  getCurrentUser(): Promise<ApiUser> {
    return ChatAPIClass.getCurrentUser();
  }

  static async getCurrentUser(): Promise<ApiUser> {
    try {
      return await this.makeRequest<ApiUser>('/auth/user', {
        method: 'POST',
        body: JSON.stringify({ user_context: {} }),
      });
    } catch (error) {
      console.error('Get user info error:', error);
      if (error instanceof Error && error.message.includes('401')) {
        throw new Error('Authentication required');
      }
      throw error;
    }
  }

  /**
   * Send a chat message and get a streaming response
   * Works with the FastAPI /chat/stream/agent endpoint
   */
  sendMessageStream(
    message: string,
    authNo?: string,
    threadId?: string,
    onChunk?: (chunk: string) => void,
    onStatus?: (status: string) => void,
    onGrade?: (gradeData: any) => void,
    selectedDocuments?: string[],
    onToolContent?: (toolContent: any[]) => void,
    sessionId?: string,
    uiMode?: string,
    onThinking?: (chunk: string) => void,
    onTodos?: (todos: TodoItem[]) => void
  ): Promise<{ response: string; thread_id: string; turn_id: string | null; is_graded: boolean }> {
    return ChatAPIClass.sendMessageStream(message, authNo, threadId, onChunk, onStatus, onGrade, selectedDocuments, onToolContent, sessionId, uiMode, onThinking, onTodos);
  }

  static async sendMessageStream(
    message: string,
    authNo?: string,
    threadId?: string,
    onChunk?: (chunk: string) => void,
    onStatus?: (status: string) => void,
    onGrade?: (gradeData: any) => void,
    selectedDocuments?: string[],
    onToolContent?: (toolContent: any[]) => void,
    sessionId?: string,
    uiMode?: string,
    onThinking?: (chunk: string) => void,
    onTodos?: (todos: TodoItem[]) => void
  ): Promise<{ response: string; thread_id: string; turn_id: string | null; is_graded: boolean }> {
    try {
      const request: ChatRequest = {
        message,
        user_context: {},
        thread_id: threadId,
        session_id: sessionId || crypto.randomUUID(),
        view_context: {
          visible_data: {
            auth_no: authNo,
            selected_documents: selectedDocuments,
            ui_mode: uiMode,
          },
        },
      };

      const authHeaders = await this.getAuthHeaders();

      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          Accept: 'application/x-ndjson',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const responseBody = await response.text();
        let errorDetail = responseBody;
        try {
          const errorResponse = JSON.parse(responseBody) as { detail?: unknown };
          if (typeof errorResponse.detail === 'string') {
            errorDetail = errorResponse.detail;
          }
        } catch {
          // Keep the raw response body when the API does not return JSON.
        }
        throw new Error(
          `API Error: ${response.status} - ${errorDetail || response.statusText}`
        );
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Failed to get response reader');
      }

      let fullResponse = '';
      let receivedThreadId = threadId || ''; // Use provided threadId or empty string as fallback
      let receivedTurnId: string | null = null;
      let isGraded = false;
      const decoder = new TextDecoder();
      let buffer = '';

      const processLine = (line: string): void => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;
        try {
          const streamMessage = JSON.parse(trimmedLine) as StreamMessage;
          const content = streamMessage.content;
          if (streamMessage.type === 'stream_end' || streamMessage.type === 'message_end') return;
          if (streamMessage.type === 'turn_metadata') {
            receivedTurnId = typeof streamMessage.turn_id === 'string' ? streamMessage.turn_id : null;
            isGraded = streamMessage.is_graded === true;
          } else if (streamMessage.type === 'thread_id' && typeof content === 'string') {
            receivedThreadId = content;
          } else if (streamMessage.type === 'status' && typeof content === 'string') {
            onStatus?.(content);
          } else if (streamMessage.type === 'grade' && content) {
            onGrade?.(content);
          } else if (streamMessage.type === 'tool_content' && Array.isArray(content)) {
            onToolContent?.(content);
          } else if (streamMessage.type === 'thinking' && typeof content === 'string') {
            onThinking?.(content);
          } else if (streamMessage.type === 'todos' && Array.isArray(content)) {
            onTodos?.(content as TodoItem[]);
          } else if (streamMessage.type === 'error' && typeof content === 'string') {
            console.error('Stream error received:', content);
            fullResponse += `\n\nError: ${content}`;
            onChunk?.(`\n\nError: ${content}`);
          } else if (streamMessage.type === 'warning' && typeof content === 'string') {
            console.warn('Stream warning received:', content);
            fullResponse += `\n\nWarning: ${content}`;
            onChunk?.(`\n\nWarning: ${content}`);
          } else if (typeof content === 'string') {
            fullResponse += content;
            onChunk?.(content);
          }
        } catch (parseError) {
          console.warn('Failed to parse NDJSON stream line:', trimmedLine, 'Parse error:', parseError);
        }
      };

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            buffer += decoder.decode();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split(/\r?\n/);
          buffer = lines.pop() || '';
          lines.forEach(processLine);
        }
        processLine(buffer);
      } finally {
        reader.releaseLock();
      }

      return {
        response: fullResponse,
        thread_id: receivedThreadId,
        turn_id: receivedTurnId,
        is_graded: isGraded,
      };
    } catch (error) {
      console.error('Chat Stream API Error:', error);
      throw new Error('Failed to send message. Please try again.');
    }
  }

  /**
   * Get grading data for a thread or session (requires authentication)
   * Supports both thread_id (backward compatibility) and session_id (new architecture)
   */
  static async getGradingData(
    threadId: string,
    options: { sessionId?: string; turnCorrelationId?: string } = {}
  ): Promise<GradingQueryResponse> {
    try {
      const request: GradingQueryRequest = {
        user_context: {},
        thread_id: threadId,
        session_id: options.sessionId,
        turn_correlation_id: options.turnCorrelationId,
      };
      
      const response = await this.makeRequest<GradingQueryResponse>('/grading/query', {
        method: 'POST',
        body: JSON.stringify(request),
      });
      return response;
    } catch (error) {
      console.error('Get grading data error:', error);
      if (error instanceof Error && error.message.includes('401')) {
        throw new Error('Authentication required');
      }
      throw error;
    }
  }

  getGradingData(
    threadId: string,
    options: { sessionId?: string; turnCorrelationId?: string } = {}
  ): Promise<GradingQueryResponse> {
    return ChatAPIClass.getGradingData(threadId, options);
  }

  /**
   * Health check endpoint
   */
  healthCheck(): Promise<HealthResponse> {
    return ChatAPIClass.healthCheck();
  }

  static async healthCheck(): Promise<HealthResponse> {
    try {
      return await this.makeRequest<HealthResponse>('/health');
    } catch (error) {
      console.error('Health check failed:', error);
      return {
        status: 'unhealthy',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Get auth token for instance methods
   */
  private async getAuthToken(): Promise<string | null> {
    if (ChatAPIClass.authProvider) {
      return await ChatAPIClass.authProvider.getToken();
    }
    return null;
  }

  /**
   * Search for records that match the search term
   * Works for any domain (authorization, complaint, etc.)
   * Requires authentication
   */
  async searchRecords(
    domain: string,
    searchTerm: string, 
    limit: number = 10, 
    signal?: AbortSignal
  ): Promise<{ records: string[] }> {
    try {
      const token = await this.getAuthToken();
      
      const requestOptions: RequestInit = {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal, // Add abort signal support
      };

      const params = new URLSearchParams({
        search_term: searchTerm,
        limit: limit.toString()
      });

      const response = await fetch(
        `${this.baseURL}/autocomplete/${domain}?${params}`,
        requestOptions
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        }
        let detail = response.statusText;
        const errorText = await response.text();
        if (errorText) {
          try {
            const errorBody = JSON.parse(errorText) as { detail?: string };
            detail = errorBody.detail || errorText;
          } catch {
            detail = errorText;
          }
        }
        throw new Error(`Search failed (${response.status}): ${detail}`);
      }

      const data = await response.json();
      return { records: data.records };
    } catch (error) {
      console.error('Record search error:', error);
      if (error instanceof Error && error.name === 'AbortError') {
        throw error; // Re-throw abort errors as-is
      }
      throw error instanceof Error ? error : new Error('Search failed. Please try again.');
    }
  }

  /**
   * Legacy method for backward compatibility
   * Delegates to searchRecords with 'authorization' domain
   */
  async searchAuthorizations(
    searchTerm: string, 
    limit: number = 10, 
    signal?: AbortSignal
  ): Promise<{ authorizations: string[] }> {
    const result = await this.searchRecords('authorization', searchTerm, limit, signal);
    return { authorizations: result.records };
  }

  /**
   * Get list of documents for a specific record ID (authorization, complaint, etc.)
   * Requires authentication
   */
  getDocuments(authorizationId: string, uiMode: string = 'authorization'): Promise<DocumentListResponse> {
    return ChatAPIClass.getDocuments(authorizationId, uiMode);
  }

  warmupRecord(recordId: string, uiMode: string): Promise<Record<string, unknown>> {
    return ChatAPIClass.warmupRecord(recordId, uiMode);
  }

  static async warmupRecord(recordId: string, uiMode: string): Promise<Record<string, unknown>> {
    return this.makeRequest<Record<string, unknown>>(
      `/records/${encodeURIComponent(uiMode)}/${encodeURIComponent(recordId)}/warmup`,
      { method: 'POST' },
    );
  }

  static async getDocuments(authorizationId: string, uiMode: string = 'authorization'): Promise<DocumentListResponse> {
    try {
      console.log(`[API] getDocuments called with authorizationId: ${authorizationId}, uiMode: ${uiMode}`);
      const request: DocumentListRequest = { 
        authorization_id: authorizationId,
        ui_mode: uiMode
      };
      console.log(`[API] Request payload:`, request);
      
      return await this.makeRequest<DocumentListResponse>('/documents/list', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    } catch (error) {
      console.error('Document list API Error:', error);
      if (error instanceof Error && error.message.includes('401')) {
        throw new Error('Authentication required to access documents.');
      }
      throw new Error('Failed to retrieve documents. Please try again.');
    }
  }

  /**
   * Download a specific document
   * Requires authentication
   */
  downloadDocument(authorizationId: string, documentGuid: string, documentName: string, uiMode: string = 'authorization'): Promise<Blob> {
    return ChatAPIClass.downloadDocument(authorizationId, documentGuid, documentName, uiMode);
  }

  static async downloadDocument(authorizationId: string, documentGuid: string, documentName: string, uiMode: string = 'authorization'): Promise<Blob> {
    try {
      const request: DocumentDownloadRequest = {
        authorization_id: authorizationId,
        document_guid: documentGuid,
        document_name: documentName,
        ui_mode: uiMode
      };

      const authHeaders = await this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/documents/download`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required to download documents.');
        }
        throw new Error(`Download failed: ${response.statusText}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('Document download API Error:', error);
      if (error instanceof Error && error.message.includes('401')) {
        throw new Error('Authentication required to download documents.');
      }
      throw new Error('Failed to download document. Please try again.');
    }
  }

  /** Download a generated CCR summary PDF for display in Aurora. */
  getCcrSummaryPdf(summaryId: string, generatedAt?: string, complaintId?: string, accessToken?: string): Promise<Blob> {
    return ChatAPIClass.getCcrSummaryPdf(summaryId, generatedAt, complaintId, accessToken);
  }

  static async getCcrSummaryPdf(summaryId: string, generatedAt?: string, complaintId?: string, accessToken?: string): Promise<Blob> {
    const query = new URLSearchParams();
    if (generatedAt) query.set('generated_at', generatedAt);
    if (complaintId) query.set('complaint_id', complaintId);
    const queryString = query.size ? `?${query.toString()}` : '';
    const endpoint = `/documents/ccr-summary/${encodeURIComponent(summaryId)}${queryString}`;
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: await this.getAuthHeaders(accessToken),
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('You are not authorized to view this summary PDF.');
      }
      if (response.status === 404) {
        throw new Error('This summary PDF is no longer available.');
      }
      throw new Error(`Summary PDF request failed: ${response.status}`);
    }

    return response.blob();
  }

  /** Download a generated authorization summary PDF for display in Aurora. */
  getAuthorizationSummaryPdf(summaryId: string, generatedAt?: string, accessToken?: string): Promise<Blob> {
    return ChatAPIClass.getAuthorizationSummaryPdf(summaryId, generatedAt, accessToken);
  }

  static async getAuthorizationSummaryPdf(summaryId: string, generatedAt?: string, accessToken?: string): Promise<Blob> {
    const query = new URLSearchParams();
    if (generatedAt) query.set('generated_at', generatedAt);
    const queryString = query.size ? `?${query.toString()}` : '';
    const endpoint = `/documents/authorization-summary/${encodeURIComponent(summaryId)}${queryString}`;
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: await this.getAuthHeaders(accessToken),
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('You are not authorized to view this summary PDF.');
      }
      if (response.status === 404) {
        throw new Error('This summary PDF is no longer available.');
      }
      throw new Error(`Summary PDF request failed: ${response.status}`);
    }

    return response.blob();
  }

  /**
   * Get user's conversation threads (requires authentication)
   */
  getUserThreads(): Promise<ThreadListResponse> {
    return ChatAPIClass.getUserThreads();
  }

  static async getUserThreads(): Promise<ThreadListResponse> {
    try {
      const response = await this.makeRequest<{
        threads: Record<string, unknown>[];
        count: number;
      }>('/conversations', {
        method: 'POST',
        body: JSON.stringify({ user_context: {}, active_only: false, limit: 100 }),
      });
      console.log('[ChatAPI] getUserThreads response:', response);
      return {
        threads: response.threads.map(normalizeUserThread),
        count: response.count,
      };
    } catch (error) {
      console.error('Get user threads API Error:', error);
      if (error instanceof Error && error.message.includes('401')) {
        throw new Error('Authentication required to access conversation history.');
      }
      throw new Error('Failed to retrieve conversation history. Please try again.');
    }
  }

  /**
   * Get conversation messages for a specific thread (requires authentication)
   */
  getThreadMessages(threadId: string): Promise<ThreadMessagesResponse> {
    return ChatAPIClass.getThreadMessages(threadId);
  }

  static async getThreadMessages(threadId: string): Promise<ThreadMessagesResponse> {
    try {
      const response = await this.makeRequest<{
        thread_id: string;
        messages: Array<{
          role: string;
          content: Array<{ content: string }>;
          created_at: string;
        }>;
        count: number;
      }>('/conversations/messages', {
        method: 'POST',
        body: JSON.stringify({ user_context: {}, thread_id: threadId }),
      });

      return {
        thread_id: response.thread_id,
        count: response.count,
        messages: response.messages.map(message => ({
          role: message.role === 'user' ? 'user' : 'assistant',
          content: message.content.map(content => content.content).join('\n'),
          timestamp: message.created_at,
        })),
      };
    } catch (error) {
      console.error('Get thread messages API Error:', error);
      if (error instanceof Error && error.message.includes('401')) {
        throw new Error('Authentication required to access conversation messages.');
      }
      if (error instanceof Error && error.message.includes('403')) {
        throw new Error('Access denied to this conversation thread.');
      }
      if (error instanceof Error && error.message.includes('404')) {
        throw new Error('Conversation thread not found.');
      }
      throw new Error('Failed to retrieve conversation messages. Please try again.');
    }
  }

  /**
   * Submit authorization ID for adhoc processing (requires authentication)
   */
  processAdhoc(authorizationId: string): Promise<AdhocProcessResponse> {
    return ChatAPIClass.processAdhoc(authorizationId);
  }

  static async processAdhoc(authorizationId: string): Promise<AdhocProcessResponse> {
    try {
      const request: AdhocProcessRequest = { authorization_id: authorizationId };
      return await this.makeRequest<AdhocProcessResponse>('/adhoc/process', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    } catch (error) {
      console.error('Process adhoc API Error:', error);
      if (error instanceof Error && error.message.includes('401')) {
        throw new Error('Authentication required to submit processing requests.');
      }
      throw new Error('Failed to submit processing request. Please try again.');
    }
  }

  /**
   * Get adhoc processing status records (requires authentication)
   */
  getProcessingStatus(limit: number = 100): Promise<ProcessingStatusResponse> {
    return ChatAPIClass.getProcessingStatus(limit);
  }

  static async getProcessingStatus(limit: number = 100): Promise<ProcessingStatusResponse> {
    try {
      const endpoint = `/adhoc/status?limit=${limit}`;
      return await this.makeRequest<ProcessingStatusResponse>(endpoint);
    } catch (error) {
      console.error('Get processing status API Error:', error);
      if (error instanceof Error && error.message.includes('401')) {
        throw new Error('Authentication required to view processing status.');
      }
      throw new Error('Failed to retrieve processing status. Please try again.');
    }
  }
}

export const ChatAPI = new ChatAPIClass();
export default ChatAPI;
